"use client";
import React, { useState, useEffect } from "react";

import { QuestionCircleOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { Form, Input, InputNumber, Row, Col, Popover, Button } from "antd";
import Cookies from "js-cookie";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";

import { CustomModal } from "@/component/CustomModal/page";
import { PUBLIC_UPDATE_RETIREMENT_CALCULATOR } from "@/graphql/mutation/stocks";
import { compareObjects } from "@/lib/compaireObject";
import {
  GENERAL_TEXT,
  FORM_FIELDS,
  URLS,
  CURRENCY_FIELDS,
  PERCENTAGE_FIELDS,
  CHART_LINE,
  RETIREMENT_CALCULATOR_FIELDS,
  USER_INFO_FIELDS,
  FIELD_TYPES,
} from "@/lib/constants";
import LockCard from "@/lib/LockCard";
import { TOOLTIPS } from "@/lib/tooltip";

import RetirementChart from "../RetirementChart";

const HeaderModal = ({ toggle, setToggle, setShowModal, showModal }) => {
  const xToken = Cookies.get("x-token");

  const title = toggle.calculator
    ? GENERAL_TEXT.FILL_THE_FORM
    : toggle.portfolio
      ? GENERAL_TEXT.PORTFOLIO_TRACKER
      : GENERAL_TEXT.WATCHLIST;

  const btnText = toggle.calculator
    ? GENERAL_TEXT.CONTINUE
    : toggle.portfolio || toggle.watchlist
      ? xToken
        ? GENERAL_TEXT.START_INVESTING
        : GENERAL_TEXT.START_INVESTING_FOR_FREE
      : "";

  const successMessage =
    " No! Based on your account size and lifestyle cost, you will not run out of money.";

  const [calculatorData, setCalculatorData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [step, setStep] = useState(0);
  const [inputData, setInputData] = useState({});
  const [chartData, setChartData] = useState({});
  const [showBtn, setShowBtn] = useState(true);
  const [form] = Form.useForm();
  const currentAge = Form.useWatch("currentAge", form);
  const retirementAge = Form.useWatch("retirementAge", form);

  useEffect(() => {
    const storedCalculatorData = localStorage.getItem("calculatorData");
    const storedUserData = localStorage.getItem("userData");
    const storedChartData = localStorage.getItem("chartData");

    if (storedCalculatorData) {
      setCalculatorData(JSON.parse(storedCalculatorData));
    }
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    }
    if (storedChartData) {
      const parsedChartData = JSON.parse(storedChartData);
      setChartData(parsedChartData);
    }
  }, []);

  useEffect(() => {
    const storedChartData = localStorage.getItem("chartData");
    if (storedChartData) {
      const Data = JSON.parse(storedChartData);
      setChartData(Data);
    }
  }, [step]);

  const [updateRetirementCalc] = useMutation(
    PUBLIC_UPDATE_RETIREMENT_CALCULATOR,
  );

  useEffect(() => {
    form.setFields(FORM_FIELDS);

    form.setFieldsValue({
      inflationRate: 3,
    });

    if (calculatorData) {
      form.setFieldsValue(calculatorData);
    }

    if (userData) {
      form.setFieldsValue(userData);
    }
  }, [form, calculatorData, userData]);

  const yearsLeft =
    typeof retirementAge === "number" && typeof currentAge === "number"
      ? Math.max(retirementAge - currentAge, 0)
      : "";

  useEffect(() => {
    form.setFieldsValue({
      yearsLeftInRetirement: yearsLeft,
    });
  }, [yearsLeft, form]);

  const resetToggle = () => {
    setToggle({ calculator: false, portfolio: false, watchlist: false });
    setShowModal(false);
  };

  const handleRemoveFocus = async () => {
    const error = await form.getFieldsError();
    const errorFields = error.filter(({ errors }) => errors.length > 0);
    if (Object.keys(chartData).length !== 0 && errorFields?.length === 0) {
      const values = await form.getFieldValue();
      if (values?.retirementAge && values?.retirementAge < values?.currentAge) {
        toast.error("Retirement age must be greater than current age.");
        return;
      }
      if (compareObjects(calculatorData, values)) {
        return;
      }
      localStorage.setItem("calculatorData", JSON.stringify(values));
      updateRetirementCalc({
        variables: {
          input: { ...values, ...userData },
        },
      }).then((data) => {
        const newChartData = data?.data?.updatePublicRetirementCalc;
        setChartData(newChartData);
        localStorage.setItem("chartData", JSON.stringify(newChartData));
        setShowBtn(!showBtn);
      });
    }
  };

  const handleOk = async () => {
    if (toggle.calculator) {
      const values = await form.validateFields();
      if (step === 1) {
        if (!compareObjects(userData, values) || showBtn) {
          localStorage.setItem("userData", JSON.stringify(values));
          setInputData({ ...inputData, ...values });
          updateRetirementCalc({
            variables: {
              input: { ...inputData, ...values },
            },
          }).then((data) => {
            const newChartData = data?.data?.updatePublicRetirementCalc;
            setChartData(newChartData);
            localStorage.setItem("chartData", JSON.stringify(newChartData));
            setStep(2);
            setShowBtn(!showBtn);
          });
        } else {
          setStep(2);
        }
      } else if (step === 2) {
        setStep(0);
        resetToggle();
      } else {
        if (userData) {
          setShowBtn(!compareObjects(calculatorData, values));
        }
        if (
          values?.retirementAge &&
          values?.retirementAge < values?.currentAge
        ) {
          toast.error("Retirement age must be greater than current age.");
          return;
        }
        localStorage.setItem("calculatorData", JSON.stringify(values));
        setInputData(values);
        setStep(1);
      }
    } else {
      resetToggle();
      window.open(URLS?.TYKR_SIGNUP, "_blank");
    }
  };

  const renderLabel = (label, key) => (
    <>
      {label}
      <Popover content={<p>{TOOLTIPS[key]}</p>} title={label}>
        <QuestionCircleOutlined className="cursor-pointer ml-4" />
      </Popover>
    </>
  );

  const renderCurrencyInput = (name, labelKey, labelText) => (
    <Col key={name} span={24} md={{ span: 12 }} lg={{ span: 12 }}>
      <Form.Item
        name={name}
        label={renderLabel(labelText, labelKey)}
        rules={[
          {
            required: true,
            message: `Please insert ${labelText.toLowerCase()}`,
          },
        ]}
        className="mb-8 fs-16"
      >
        <InputNumber
          min={0}
          className="w-100"
          addonBefore="$"
          onBlur={handleRemoveFocus}
        />
      </Form.Item>
    </Col>
  );

  const renderPercentageInput = (
    name,
    labelKey,
    labelText,
    disabled = false,
  ) => (
    <Col key={name} span={24} md={{ span: 12 }} lg={{ span: 12 }}>
      <Form.Item
        name={name}
        label={renderLabel(labelText, labelKey)}
        rules={
          disabled
            ? []
            : [
                {
                  required: true,
                  message: `Please insert ${labelText.toLowerCase()}`,
                },
              ]
        }
        className="mb-8"
      >
        <InputNumber
          min={0}
          max={100}
          value={form.getFieldValue(name)}
          className="w-100"
          disabled={disabled}
          addonAfter="%"
          onBlur={handleRemoveFocus}
        />
      </Form.Item>
    </Col>
  );

  const renderField = (field) => {
    const commonProps = {
      name: field.name,
      label: field.label,
      rules: field.rules,
      className: field.className,
    };

    const inputProps = {
      className: field.className,
      disabled: field.disabled,
    };

    const fieldElement =
      field.type === FIELD_TYPES.NUMBER ? (
        <InputNumber
          min={field.min}
          onBlur={field.onBlur ? handleRemoveFocus : undefined}
          {...inputProps}
        />
      ) : (
        <Input placeholder={field.placeholder} {...inputProps} />
      );

    return (
      <Col
        key={field.name}
        span={24}
        md={{ span: field.type === FIELD_TYPES.NUMBER ? 12 : 24 }}
        lg={{ span: field.type === FIELD_TYPES.NUMBER ? 12 : 24 }}
      >
        <Form.Item
          {...commonProps}
          className={field.type === FIELD_TYPES.NUMBER ? "mb-8" : ""}
        >
          {fieldElement}
        </Form.Item>
      </Col>
    );
  };

  const calculationBlock = (title, value) => (
    <div className="calculation">
      <p className="header-title">
        {title}
        <Popover content={<div>{TOOLTIPS[title]}</div>} title={title}>
          <QuestionCircleOutlined className="cursor-pointer" />
        </Popover>
      </p>
      <div className="portfolio-graph-title">
        <span className="symbol-sign mr-3">$</span>
        {value ? (
          <NumericFormat
            value={value.toFixed(0)}
            displayType="text"
            thousandSeparator
            decimalScale={0}
          />
        ) : (
          ""
        )}
      </div>
    </div>
  );

  const displayBlock = (title) => (
    <div className="calculation">
      <p className="header-title">
        {title}
        <Popover content={<div>{TOOLTIPS[title]}</div>} title={title}>
          <QuestionCircleOutlined className="cursor-pointer" />
        </Popover>
      </p>
      <div className="portfolio-graph-title d-flex">
        <span className="symbol-sign mr-3">$</span>
        <div className="display-block-div"></div>
      </div>
    </div>
  );

  return (
    <CustomModal
      title={step === 2 ? "Calculations" : title}
      okText={step ? "Show results" : btnText}
      className={`modal ${step === 2 || step === 1 || Object.keys(chartData).length !== 0 ? "header-modal" : "edit-modal"} ${toggle.calculator ? ((step === 1 && !showBtn) || step === 2 || Object.keys(chartData).length !== 0 ? "step-modal" : "rename-modal") : ""}`}
      open={showModal}
      centered
      onOk={handleOk}
      onCancel={() => {
        setStep(0);
        setShowModal(false);
        resetToggle();
      }}
      footer={
        <>
          {/* Back Button */}
          {step === 2 && (
            <Button
              onClick={() => setStep(0)}
              type="default"
              className="back-btn"
            >
              Back
            </Button>
          )}

          {/* OK Button */}
          {!toggle.calculator ||
          (!(step === 2) && Object.keys(chartData).length === 0) ? (
            <Button
              onClick={handleOk}
              className={` ${!toggle.calculator ? "w-100" : ""}`}
              type="primary"
            >
              {step ? "Show results" : btnText}
            </Button>
          ) : null}
        </>
      }
    >
      {toggle.calculator && (
        <div className="mt-16">
          {step === 1 ? (
            <Row gutter={[16]}>
              <Col xs={24} md={24} lg={8}>
                <div>
                  <div className="second-hint">
                    <p className="modal-text">
                      💡 <span className="modal-text-span">Note:</span> Enter
                      your name and email to see if you are on track to retire.
                    </p>
                  </div>
                  <Form form={form} layout="vertical" className="mt-24">
                    <Row gutter={[16]}>{USER_INFO_FIELDS.map(renderField)}</Row>
                  </Form>
                </div>
              </Col>
              <Col xs={24} md={24} lg={16}>
                <div className="calculation-div calculation-block">
                  <div className="d-flex retirement-amount calculation-block mb-16">
                    {displayBlock("Account Size at Retirement")}
                    <div className="calculation-divider" />
                    {displayBlock("Lifestyle Cost After Retirement")}
                  </div>
                  <div className="bg-image mt-16">
                    <LockCard onlyButton={false} />
                  </div>
                </div>
              </Col>
            </Row>
          ) : step === 2 ? (
            <>
              <div className="calculation-div">
                <div className="d-flex justify-content-between w-100 calculation-block-header">
                  <div className="d-flex retirement-amount calculation-block">
                    {calculationBlock(
                      "Account Size at Retirement",
                      chartData?.accountSizeAtRetirement,
                    )}
                    <div className="calculation-divider" />
                    {calculationBlock(
                      "Lifestyle Cost After Retirement",
                      chartData?.lifeStyleCostAfterRetirement,
                    )}
                  </div>
                  <Row gutter={16} className="projection-info">
                    {CHART_LINE.map((line) => (
                      <Col
                        key={line?.name}
                        className="d-flex projection-lines align-items-center"
                      >
                        <div
                          className="projection-icon"
                          style={{ backgroundColor: line?.color }}
                        ></div>
                        <div className="projection-name">{line?.name}</div>
                      </Col>
                    ))}
                  </Row>
                </div>
                <RetirementChart
                  chartData={chartData}
                  height={350}
                  width={650}
                />
                <div
                  className={` ${chartData?.willRunOutOfMoney ? "error-message-box" : "success-message-box"}`}
                >
                  💵 <b> Will you run out of money?</b>
                  {chartData?.willRunOutOfMoney
                    ? ` Yes, you will run out of money at age ${chartData?.runOutOfMoneyAtAge} based on your account size and lifestyle cost. You may have to adjust Your Goal Retirement Age, Years in Retirement, Current Annual Lifestyle Cost, or Annual Contributions.`
                    : successMessage}
                </div>
              </div>
            </>
          ) : (
            <Row gutter={16}>
              <Col
                xs={24}
                md={24}
                lg={Object.keys(chartData).length !== 0 ? 12 : 24}
              >
                <div className="second-hint">
                  <p className="modal-text">
                    📈{" "}
                    <span className="modal-text-span fs-12">
                      Personalize the outcome:
                    </span>{" "}
                    Add your information to update the chart
                  </p>
                </div>
                <Form form={form} layout="vertical" className="mt-16">
                  <Row gutter={[16]}>
                    {RETIREMENT_CALCULATOR_FIELDS.map(renderField)}
                    {CURRENCY_FIELDS.map(({ name, id, label }) =>
                      renderCurrencyInput(name, id, label),
                    )}
                    {PERCENTAGE_FIELDS.map(({ name, id, label, isLast }) =>
                      renderPercentageInput(name, id, label, isLast),
                    )}
                  </Row>
                </Form>
              </Col>
              {Object.keys(chartData).length !== 0 && (
                <Col xs={24} md={24} lg={12}>
                  <div className="calculation-div">
                    <div className=" w-100">
                      <div className="d-flex retirement-amount calculation-block">
                        {calculationBlock(
                          "Account Size at Retirement",
                          chartData?.accountSizeAtRetirement,
                        )}
                        <div className="calculation-divider" />
                        {calculationBlock(
                          "Lifestyle Cost After Retirement",
                          chartData?.lifeStyleCostAfterRetirement,
                        )}
                      </div>
                      <Row gutter={16} className="projection-info">
                        {CHART_LINE.map((line) => (
                          <Col
                            key={line?.name}
                            className="d-flex projection-lines align-items-center"
                          >
                            <div
                              className="projection-icon"
                              style={{ backgroundColor: line?.color }}
                            ></div>
                            <div className="projection-name">{line?.name}</div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                    <RetirementChart chartData={chartData} height={350} />
                    <div
                      className={` ${chartData?.willRunOutOfMoney ? "error-message-box" : "success-message-box"}`}
                    >
                      💵 <b> Will you run out of money?</b>
                      {chartData?.willRunOutOfMoney
                        ? ` Yes, you will run out of money at age ${chartData?.runOutOfMoneyAtAge} based on your account size and lifestyle cost. You may have to adjust Your Goal Retirement Age, Years in Retirement, Current Annual Lifestyle Cost, or Annual Contributions.`
                        : successMessage}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          )}
        </div>
      )}

      {toggle.portfolio && (
        <div className="fs-16">
          See the rating of your portfolio. Join Tykr{xToken ? "" : " for free"}
          .
        </div>
      )}
      {toggle.watchlist && (
        <div className="fs-16">
          Automatically receive notifications when you add stocks to your
          watchlists.
        </div>
      )}
    </CustomModal>
  );
};

export default HeaderModal;
